//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  DockDoorManagementGlobalStyle,
} from './DockDoorManagement.styles';

const hero = {
  slug: 'dock-door-management',
  title: 'Dock Door Management',
  description:
    'Runs the day-to-day flow of trucks in and out of your docks, handling late arrivals, no-shows and dock closures, and keeps the day on plan with daily dock briefings, appointment reminders and automatic appointment confirmations.',
  metric: '20–30%',
  metricLabel: 'better dock utilization',
};

const watchItRun = {
  title: 'A dock door just went down',
  stageHeadTitle: 'Door 4 just went down, with six trucks due before 3pm.',
  stageHeadMeta: 'Dock · DC-02',
  rows: [
    {
      time: '12:40:00',
      text: 'Door 4 marked down until 3pm. 6 appointments affected.',
    },
    {
      time: '12:40:06',
      text: 'Found compatible doors with open capacity for 5 of them.',
    },
    {
      time: '12:40:20',
      text: 'Move plan sent to the scheduler, and approved.',
    },
    {
      time: '12:41:05',
      text: '5 appointments moved. Each carrier notified of its new door.',
    },
    {
      time: '12:41:30',
      text: 'No compatible door left for the 6th. Sent to the scheduler with 3 open slots tomorrow.',
    },
  ],
  summaryText:
    'A dock door went down in the middle of the day. Loadsmart AI found compatible doors for five of the six trucks due there, moved them once the scheduler approved the plan, told each carrier, and gave the scheduler three slots to offer the one it couldn’t place.',
  agentBadge: 'Dock Agent',
  escalationTitle: 'You decide how much it does on its own.',
  escalationText:
    'Each automation runs at the level you set: notify only, propose and wait for approval, or act. Briefings and reminders can go out on their own, while no-shows, moves and confirmations wait for a scheduler’s approval until you’re ready to let them run.',
};

const howItWorks = {
  stepsTitleWhite: 'Runs the dock schedule all day,',
  stepsTitleGradient: 'from the morning briefing to the last door.',
  steps: [
    {
      title: 'Briefs the day',
      description:
        'Sends a dock plan before the first shift, with overbooked hours and appointments missing details flagged.',
    },
    {
      title: 'Confirms and reminds',
      description:
        'Confirms requested appointments that pass your rules, and reminds carriers before arrival, including any missing reference numbers.',
    },
    {
      title: 'Handles late arrivals and no-shows',
      description:
        'Moves a late truck to the next valid slot, and marks a no-show once the facility’s late threshold passes, freeing the door and offering the carrier new slots.',
    },
    {
      title: 'Rebalances when a door goes down',
      description:
        'Moves every affected appointment to compatible doors in one plan, and reports any it couldn’t place.',
    },
  ],
  outcomes: [
    {
      title: 'Doors stay in use',
      description:
        'A door freed by a no-show or a closure goes back on the schedule while there is still time to fill it.',
    },
    {
      title: 'The shift starts ahead',
      description:
        'Schedulers know the day’s problem hours before the first truck arrives.',
    },
    {
      title: 'Carriers arrive ready',
      description:
        'Reminders collect missing reference numbers before the truck reaches the gate.',
    },
  ],
};

const lineupCards = [
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description: 'Books and rebooks the appointments this agent keeps on plan.',
  },
  {
    slug: 'detention-handling',
    title: 'Detention Handling',
    description: 'Watches each truck’s free time once it’s on site.',
  },
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description:
      'We capture every quote, tender, rate con, BOL, POD and invoice as it arrives, and file it against the right shipment automatically.',
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & Status Updates',
    description:
      'We get status from carriers so your team stops making check calls, and push clean updates to the people who need them.',
  },
  {
    slug: 'tender-failures',
    title: 'Tender Failures',
    description:
      'When a tender falls through, we re-tender down your routing guide and re-book, before the miss ever reaches your team.',
  },
  {
    slug: 'proactive-load-audits',
    title: 'Proactive Load Audits',
    description:
      'We check every load before pickup and flag at-risk transit early, so problems surface before they turn into penalties.',
  },
  {
    slug: 'claims-processing',
    title: 'Claims Processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims get filed and recovered.',
  },
  {
    slug: 'purchase-order-follow-up',
    title: 'Purchase Order Follow-Up',
    description:
      'We find purchase orders due with no appointment, and get the supplier or carrier to book before stock runs short.',
  },
  {
    slug: 'warehouse-customer-service',
    title: 'Warehouse Customer Service',
    description:
      'We answer your warehouse’s status questions and book appointments from an email, using your dock schedule and WMS.',
  },
  {
    slug: 'yard-task-orchestration',
    title: 'Yard Task Orchestration',
    description:
      'We create, assign and sequence yard tasks, from moves to temperature checks, and tell suppliers and carriers when something needs them.',
  },
];

function DockDoorManagement() {
  return (
    <PageWrapper>
      <DockDoorManagementGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Dock Door Management" />
      <Footer />
    </PageWrapper>
  );
}

export default DockDoorManagement;
export { DockDoorManagement };
